/*
    Copyright (c) Computer-L.A.N. GmbH (http://www.computer-lan.de).
    Alle Rechte vorbehalten.
*/

import React, { ReactNode, useState } from "react";
import { Link, RouteComponentProps } from "react-router-dom";
import { Box, Button, Divider, Paper, Tabs } from "@mui/material";
import Tab, { tabClasses } from "@mui/material/Tab"
import { TabEinstellungenTab } from "../../../Cloud/Einstellungen/Commands/TabEinstellungen";
import { ThumbtackIcon, ThumbtackSlashIcon } from "../../FontAwesome";
import CommandBar from "../CommandBar";
import CommandBarSearchInput from "../CommandBarSearchInput";
import variablesScss from "../../Themes/variables.module.scss";

export type TabContainerTabsItem = {
    caption: ReactNode;
    to: string;
    icon?: React.ReactElement;
    onClick?: React.MouseEventHandler<HTMLDivElement>;
    disabled?: boolean;
    visible?: boolean;
    isSubTab?: boolean;
    selected?: boolean;
    isNotClickable?: boolean;
    matchcode?: string;
    key?: TabEinstellungenTab;
    isFavorite?: boolean;
};

export type TabContainerActions = {
    setzeFavorit: (tab: TabEinstellungenTab) => void,
    sortiereUm: (tab: TabEinstellungenTab, nachSortierung: number) => void,
}

export type TabContainerProps = RouteComponentProps & {
    children: ReactNode;
    items: TabContainerTabsItem[];
    zeigeSuche?: boolean;
    actions?: TabContainerActions;
};

function render(
    items: TabContainerTabsItem[],
    item: TabContainerTabsItem,
    idx: number,
    pathname: string,
    actions?: TabContainerActions
) {
    const Favorite = () => <>
        {(item.isFavorite === undefined || item.key === undefined) ? null
            : <Button
                component="span"
                disableRipple
                className="favorit"
                onClick={(evt) => {
                    evt.stopPropagation();
                    evt.preventDefault();
                    actions?.setzeFavorit(item.key!);
                }}
                sx={{
                    marginLeft: "auto",
                    padding: "0.25em",
                    minWidth: "20px",
                    borderRadius: "50%",
                    color: "text.primary",
                    "& > *": { visibility: "hidden" }
                }}
            >
                {item.isFavorite
                    ? <ThumbtackSlashIcon fontSize="small" />
                    : <ThumbtackIcon fontSize="small" />
                }
            </Button>
        }
    </>;

    const TabComponent = React.forwardRef((props: any, ref) =>
        <>
            {item.onClick === undefined
                ? <Link {...props} to={item.to} ref={ref}>
                    {props.children}
                    <Favorite />
                </Link>
                : <span {...props} ref={ref} onClick={(evt: React.MouseEvent<HTMLDivElement, MouseEvent>) => {
                    if (item.onClick && item.to !== pathname) {
                        item.onClick(evt);
                    }
                }}
                >
                    {props.children}
                    <Favorite />
                </span>
            }
        </>
    );

    return (
        <Tab
            key={item.key ?? idx}
            label={item.caption}
            value={item.to}
            icon={item.icon}
            iconPosition="start"
            disabled={item.disabled}
            draggable={item.isFavorite}
            component={TabComponent}
            wrapped
            onDragStart={(event: React.DragEvent<HTMLLIElement>) => {
                event.stopPropagation();

                event.dataTransfer.clearData();
                event.dataTransfer.setData("text/plain", item.key!);
                event.dataTransfer.effectAllowed = "move";
            }}
            onDragOver={(event: React.DragEvent<HTMLLIElement>) => {
                event.preventDefault();

                event.dataTransfer.dropEffect = !!item.isFavorite
                    ? "move"
                    : "none";

                if (!item.isFavorite) return;

                const rect = event.currentTarget.getBoundingClientRect();
                const midY = rect.y + rect.height / 2;

                if (event.pageY < midY) {
                    event.currentTarget.classList.remove("below");
                    event.currentTarget.classList.add("above");
                } else {
                    event.currentTarget.classList.remove("above");
                    event.currentTarget.classList.add("below");
                }
            }}
            onDragEnter={(event: React.DragEvent<HTMLLIElement>) => {
                if (!item.isFavorite) return;
                event.currentTarget.classList.add("drag");
            }}
            onDragLeave={(event: React.DragEvent<HTMLLIElement>) => {
                event.currentTarget.classList.remove("drag", "above", "below");
            }}
            onDrop={(event: React.DragEvent<HTMLLIElement>) => {
                const isAbove = event.currentTarget.classList.contains("above");
                event.currentTarget.classList.remove("drag", "above", "below");
                event.preventDefault();

                const itemIndex = items.indexOf(item);
                const sortingKey = event.dataTransfer.getData("text/plain") as TabEinstellungenTab;

                if (itemIndex === -1 || sortingKey === item.key) return;

                const sortingIndex = items.findIndex(i => i.key === sortingKey);
                const sortToBelow = sortingIndex < itemIndex;

                const newSorting = itemIndex
                    + (isAbove ? 0 : 1)
                    + (sortToBelow ? -1 : 0);

                actions?.sortiereUm(sortingKey, newSorting + 1);
            }}
            sx={[
                {
                    fontSize: "0.875rem",
                    lineHeight: 1,
                    minHeight: "3rem",
                    alignItems: "center",
                    justifyContent: "start",
                    color: "text.primary",
                    textTransform: "none",
                    textDecoration: "none",
                    border: "none",
                    pl: 1,
                    textAlign: "left",
                    "&:hover": {
                        backgroundColor: "action.hover",
                    },
                    "&:hover .favorit > *": { visibility: "visible" },
                    [`&.${tabClasses.selected}`]: {
                        color: "text.primary",
                        backgroundColor: "action.selected",
                    },
                    "&.drag *": {
                        pointerEvents: "none",
                    },
                    "&.drag": {
                        borderWidth: "0px",
                        borderColor: theme => theme.palette.secondary.main,
                        borderStyle: "solid",
                    },
                    "&.drag.above": {
                        borderTopWidth: "2px",
                        paddingTop: "7px",
                    },
                    "&.drag.below": {
                        borderBottomWidth: "2px",
                        paddingBottom: "7px",
                    },
                },
                !!item.selected && {
                    color: "text.primary",
                    backgroundColor: "action.selected",
                },
                !!item.isNotClickable && {
                    cursor: "default",
                    pointerEvents: "none",
                    "&:hover": {
                        backgroundColor: "inherit",
                    }
                },
                !!item.isSubTab && {
                    pl: 5,
                },
            ]}
        />
    );
}

export default function TabContainer(props: TabContainerProps) {
    const [suchbegriff, setzeSuchbegriff] = useState("");

    const visibleItems = props.items.filter(
        item => item.visible !== false
            && ((typeof item.caption === "string" && item.caption.toLowerCase().indexOf(suchbegriff) >= 0)
                || (item.matchcode ?? "").toLowerCase().indexOf(suchbegriff) >= 0));

    const allFavorites = props.items.filter(f => !!f.isFavorite);
    const allNonFavorites = props.items.filter(f => !f.isFavorite);
    const visibleFavorites = visibleItems.filter(f => !!f.isFavorite);
    const visibleNonFavorite = visibleItems.filter(f => !f.isFavorite);

    return (
        <Paper
            elevation={0}
            sx={{ display: "flex", flex: 1, overflow: "hidden" }}
        >
            <Box sx={[{ display: "flex", flexDirection: "column" }, (theme) => ({
                [theme.breakpoints.down("sm")]: {
                    opacity: 0,
                    minWidth: "unset",
                    maxWidth: 0,
                    overflow: "hidden",
                },
            })]}>
                {props.zeigeSuche && <CommandBar
                    search={<CommandBarSearchInput
                        onSearch={(suchbegriff) => setzeSuchbegriff(suchbegriff.toLowerCase())}
                        sx={{ ml: "0.5rem" }}
                    />}
                    sx={{ borderRight: `1px solid ${variablesScss.colorsDefaultBorder}` }}
                />}
                <Tabs
                    value={props.location.pathname}
                    orientation="vertical"
                    variant="scrollable"
                    visibleScrollbar
                    TabIndicatorProps={{ sx: { display: "none" } }}
                    sx={{
                        minWidth: "15rem",
                        borderRight: `1px solid ${variablesScss.colorsDefaultBorder}`,
                        overflowY: "auto",
                        height: "100%"
                    }}
                >
                    {visibleFavorites.map((item, idx) => render(allFavorites, item, idx, props.location.pathname, props.actions))}
                    {visibleFavorites.length > 0 && visibleNonFavorite.length > 0 && <Tab
                        icon={<Divider />}
                        disabled
                        sx={{
                            margin: 0, padding: 0, minHeight: "20px", "& > *": { width: "100%" }
                        }}
                    />}
                    {visibleNonFavorite.map((item, idx) => render(allNonFavorites, item, idx, props.location.pathname, props.actions))}
                </Tabs>
            </Box>

            {props.children}
        </Paper>
    );
}